import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API from "../../../services/api";
import "./library.css";

const today = new Date().toISOString().split("T")[0];

export default function BookCopies() {
  const { bookId } = useParams();
  const navigate   = useNavigate();
  const school     = JSON.parse(localStorage.getItem("schoolAdmin"));

  const [book,          setBook]          = useState(null);
  const [copies,        setCopies]        = useState([]);
  const [issued,        setIssued]        = useState([]);
  const [newCopyNumber, setNewCopyNumber] = useState("");
  const [loading,       setLoading]       = useState(true);
  const [adding,        setAdding]        = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const [copiesRes, issuedRes] = await Promise.all([
        API.get(`/school-admin/library/books/${bookId}/copies/`,  { headers: { "X-SCHOOL-ID": school.school.id } }),
        API.get(`/school-admin/library/books/${bookId}/issued/`,  { headers: { "X-SCHOOL-ID": school.school.id } }),
      ]);
      setBook(copiesRes.data.book    || null);
      setCopies(copiesRes.data.copies || []);
      setIssued(issuedRes.data.issued  || []);
    } catch (err) { console.error(err); alert("Failed to load book data"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [bookId]);

  const addCopy = async () => {
    if (!newCopyNumber.trim()) return alert("Enter a copy number (e.g. C-1)");
    try {
      setAdding(true);
      await API.post(
        `/school-admin/library/books/${bookId}/copies/`,
        { copy_number: newCopyNumber.trim() },
        { headers: { "X-SCHOOL-ID": school.school.id } }
      );
      setNewCopyNumber("");
      loadData();
    } catch (err) { console.error(err); alert("Failed to add copy"); }
    finally { setAdding(false); }
  };

  const available = copies.filter(c =>  c.is_available).length;
  const outCount  = copies.filter(c => !c.is_available).length;

  return (
    <div className="lb-page">

      {/* ══ HERO ══ */}
      <div className="lb-hero">
        <div className="lb-hero-left">
          <div className="lb-hero-icon">📋</div>
          <div>
            <h2 className="lb-hero-title">Copies &amp; Issues</h2>
            <p className="lb-hero-sub">
              {book ? book.title : "Loading…"}
              {!loading && <> — <span style={{ color:"#93c5fd" }}>{available} available</span> · <span style={{ color:"#fcd34d" }}>{outCount} issued</span></>}
            </p>
          </div>
        </div>
        <div className="lb-hero-right">
          <button className="lb-btn-back" onClick={() => navigate("/school-admin/library/books")}>← Books</button>
        </div>
      </div>

      {/* ══ BOOK INFO CARD ══ */}
      {book && (
        <div className="lb-book-info">
          <div className="lb-book-info-row">
            <span className="lb-book-info-label">Title</span>
            <span className="lb-book-info-value">{book.title}</span>
          </div>
          <div className="lb-book-info-row">
            <span className="lb-book-info-label">Author</span>
            <span className="lb-book-info-value">{book.author || "—"}</span>
          </div>
          <div className="lb-book-info-row">
            <span className="lb-book-info-label">Category</span>
            <span className="lb-book-info-value">
              {book.category_name
                ? <span style={{ background:"#dbeafe", color:"#1e40af", padding:"2px 10px", borderRadius:"20px", fontSize:"11px", fontWeight:700 }}>{book.category_name}</span>
                : "—"}
            </span>
          </div>
          <div className="lb-book-info-row">
            <span className="lb-book-info-label">ISBN</span>
            <span className="lb-book-info-value" style={{ fontFamily:"monospace" }}>{book.isbn || "—"}</span>
          </div>
        </div>
      )}

      {/* ══ ADD COPY ══ */}
      <div className="lb-add-copy-row">
        <span style={{ fontWeight:700, color:"#1e40af", fontSize:"13px" }}>➕ Add New Copy</span>
        <div style={{ display:"flex", gap:"10px", alignItems:"center" }}>
          <input
            placeholder="Copy number (e.g. C-1)"
            value={newCopyNumber}
            onChange={e => setNewCopyNumber(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addCopy()}
            style={{ padding:"8px 14px", border:"1.5px solid #bfdbfe", borderRadius:"10px", fontSize:"13px", outline:"none", width:"220px" }}
          />
          <button className="lb-btn-copy-add" onClick={addCopy} disabled={adding}>
            {adding ? <><div className="lb-spinner"></div> Adding…</> : "Add Copy"}
          </button>
        </div>
      </div>

      {/* ══ COPIES TABLE ══ */}
      <p className="lb-section-title">📦 Copies ({copies.length})</p>
      <div className="lb-card" style={{ marginBottom:"28px" }}>
        {loading ? (
          <div className="lb-empty"><div className="lb-empty-icon">⏳</div><strong>Loading…</strong></div>
        ) : copies.length === 0 ? (
          <div className="lb-empty">
            <div className="lb-empty-icon">📭</div>
            <strong>No copies added yet</strong>
            <p>Use the form above to add the first copy</p>
          </div>
        ) : (
          <table className="lb-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Copy Number</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {copies.map((c, idx) => (
                <tr key={c.id}>
                  <td style={{ color:"#94a3b8", fontSize:"12px" }}>{idx + 1}</td>
                  <td style={{ fontFamily:"monospace", fontWeight:700 }}>{c.copy_number}</td>
                  <td>
                    <span className={`lb-pill ${c.is_available ? "available" : "issued"}`}>
                      {c.is_available ? "✓ Available" : "📤 Issued"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ══ ISSUED STUDENTS ══ */}
      <p className="lb-section-title">👨‍🎓 Issued Students ({issued.length})</p>
      <div className="lb-card">
        {loading ? (
          <div className="lb-empty"><div className="lb-empty-icon">⏳</div><strong>Loading…</strong></div>
        ) : issued.length === 0 ? (
          <div className="lb-empty">
            <div className="lb-empty-icon">🎉</div>
            <strong>All copies are in</strong>
            <p>No students currently have this book</p>
          </div>
        ) : (
          <table className="lb-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Class</th>
                <th>Section</th>
                <th>Copy</th>
                <th>Issue Date</th>
                <th>Due Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {issued.map((i, idx) => {
                const overdue = !i.returned && i.due_date && i.due_date < today;
                return (
                  <tr key={idx}>
                    <td><strong>{i.student_name}</strong></td>
                    <td>{i.class || "—"}</td>
                    <td>{i.section || "—"}</td>
                    <td style={{ fontFamily:"monospace", fontWeight:700 }}>{i.copy_number}</td>
                    <td>{i.issue_date}</td>
                    <td style={{ color: overdue ? "#ef4444" : "inherit" }}>{i.due_date}</td>
                    <td>
                      <span className={`lb-pill ${i.returned ? "returned" : overdue ? "overdue" : "issued"}`}>
                        {i.returned ? "✓ Returned" : overdue ? "⚠ Overdue" : "📤 Issued"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}
